import { ReceiptText } from "lucide-react";

interface Transaction {
  id: string;
  merchant_name: string;
  amount: number;
  status: string;
  processed_at: string | null;
  physical_card?: {
    card_name: string;
    card_number_last_four: string;
  } | null;
  virtual_card?: {
    card_name: string;
    card_number_last_four: string;
  } | null;
}

interface TransactionListProps {
  transactions: Transaction[];
  showCard?: boolean;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "Pending";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const statusColor = (status: string): string => {
  switch (status) {
    case "completed":
      return "text-ink";
    case "pending":
    case "authorized":
      return "text-warning";
    case "declined":
    case "reversed":
      return "text-error";
    default:
      return "text-muted";
  }
};

const getMerchantInitial = (name: string): string => {
  return (name || "?").charAt(0).toUpperCase();
};

export const TransactionList = ({
  transactions,
  showCard = true,
}: TransactionListProps) => {
  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted">
        <ReceiptText size={36} className="mb-2 opacity-50" />
        <p className="text-sm">No transactions yet</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-hairline">
      {transactions.map((tx) => (
        <div key={tx.id} className="flex items-center gap-3 py-3.5">
          <div className="w-9 h-9 rounded-full bg-ground border border-hairline flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-ink">
              {getMerchantInitial(tx.merchant_name)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate text-ink">
              {tx.merchant_name || "Unknown"}
            </p>
            <p className="font-mono text-[11px] text-muted uppercase tracking-[0.04em]">
              {formatDate(tx.processed_at)}
              {showCard && (tx.physical_card || tx.virtual_card) && (
                <span> · ••{(tx.physical_card || tx.virtual_card)!.card_number_last_four}</span>
              )}
            </p>
          </div>
          <div className="text-right ml-3">
            <p className={`money font-medium text-sm ${statusColor(tx.status)}`}>
              -${Number(tx.amount).toFixed(2)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};
